import React, { useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
    LayoutDashboard, 
    Users, 
    Clock, 
    Calendar, 
    Settings, 
    LogOut, 
    Menu, 
    X,
    ChevronDown,
    Shield,
    Home,
    KeyRound,
    ClipboardList,
    MessageSquare
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { FileSearch, ShieldAlert } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import NotificationCenter from './NotificationCenter';

const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Members', href: '/admin/members', icon: Users },
    { name: 'Calendar', href: '/admin/calendar', icon: Calendar },
    { name: 'Timecard', href: '/admin/timecard', icon: ClipboardList },
    { name: 'Duty Officer Log', href: '/admin/duty-officer-timecard', icon: Shield },
];

const mobileNav = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Attendance', href: '/attendance', icon: Clock },
    { name: 'Calendar', href: '/calendar', icon: Calendar },
    { name: 'Menu', icon: Menu, isMenu: true },
];

const NavItem = ({ item, onClick }) => {
    const location = useLocation();
    const isActive = location.pathname === item.href || 
        (item.href !== '/' && location.pathname.startsWith(item.href));
    
    return (
        <NavLink
            to={item.href}
            onClick={onClick}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                isActive 
                    ? 'bg-accent text-primary border-l-2 border-primary font-medium' 
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            }`}
            data-testid={`nav-${item.name.toLowerCase()}`}
        >
            <item.icon className="w-5 h-5" />
            <span>{item.name}</span>
        </NavLink>
    );
};

const Sidebar = ({ onItemClick }) => {
    const { user, logout, hasUnitAccess, isNetworkManager } = useAuth();
    const navigate = useNavigate();
    
    return (
        <div className="flex flex-col h-full bg-card border-r border-border">
            {/* Logo */}
            <div className="p-6 border-b border-border">
                <div className="flex items-center gap-3 ses-logo">
                    <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                        <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="font-bold text-lg text-foreground leading-tight">Brisbane City</h1>
                        <p className="text-xs text-primary font-semibold">SES Unit</p>
                    </div>
                </div>
            </div>
            
            {/* Navigation */}
            <ScrollArea className="flex-1 px-4 py-6">
                <nav className="space-y-1">
                    {navigation.map((item) => (
                        <NavItem key={item.name} item={item} onClick={onItemClick} />
                    ))}
                </nav>
            </ScrollArea>
            
            {/* User Section */}
            <div className="p-4 border-t border-border space-y-3">
                {/* Return to Portal Button */}
                <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    onClick={() => {
                        if (onItemClick) onItemClick();
                        navigate('/portal');
                    }}
                    data-testid="return-to-portal-btn"
                >
                    <Home className="w-4 h-4" />
                    Return to Portal
                </Button>
                
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <Avatar className="h-10 w-10">
                        <AvatarImage src={user?.profile_image} />
                        <AvatarFallback className="bg-primary text-white">
                            {user?.first_name?.[0]}{user?.last_name?.[0]}
                        </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                            {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                            {user?.role_name}
                        </p>
                    </div>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                                <ChevronDown className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel>My Account</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem asChild>
                                <NavLink to="/settings" className="cursor-pointer">
                                    <Settings className="mr-2 h-4 w-4" />
                                    Settings
                                </NavLink>
                            </DropdownMenuItem>
                            {isNetworkManager() && (
                                <>
                                    <DropdownMenuItem asChild>
                                        <NavLink to="/admin/permissions" className="cursor-pointer">
                                            <KeyRound className="mr-2 h-4 w-4" />
                                            Permission Editor
                                        </NavLink>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <NavLink to="/admin/contact-submissions" className="cursor-pointer" data-testid="nav-contact-submissions">
                                            <MessageSquare className="mr-2 h-4 w-4" />
                                            Contact Submissions
                                        </NavLink>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <NavLink to="/admin/audit-logs" className="cursor-pointer" data-testid="nav-audit-logs">
                                            <FileSearch className="mr-2 h-4 w-4" />
                                            Audit Logs
                                        </NavLink>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <NavLink to="/admin/security" className="cursor-pointer" data-testid="nav-security-dashboard">
                                            <ShieldAlert className="mr-2 h-4 w-4" />
                                            Security Dashboard
                                        </NavLink>
                                    </DropdownMenuItem>
                                </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={logout} className="text-destructive cursor-pointer">
                                <LogOut className="mr-2 h-4 w-4" />
                                Log out
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </div>
    );
};

const Layout = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();
    
    const getPageTitle = () => {
        const path = location.pathname;
        if (path === '/') return 'Dashboard';
        const navItem = navigation.find(item => path.startsWith(item.href) && item.href !== '/');
        return navItem?.name || 'Brisbane City SES';
    };
    
    return (
        <div className="min-h-screen bg-background">
            {/* Desktop Sidebar */}
            <aside className="sidebar hidden lg:block">
                <Sidebar />
            </aside>
            
            {/* Main Content */}
            <div className="main-content">
                {/* Top Header - Mobile & Desktop */}
                <header className="sticky top-0 z-30 glass-effect border-b border-border">
                    <div className="flex items-center justify-between h-16 px-4 lg:px-8">
                        {/* Mobile Menu Button */}
                        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                            <SheetTrigger asChild className="lg:hidden">
                                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                                    <Menu className="h-5 w-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="left" className="p-0 w-80">
                                <Sidebar onItemClick={() => setMobileMenuOpen(false)} />
                            </SheetContent>
                        </Sheet>
                        
                        {/* Page Title */}
                        <h1 className="text-xl font-semibold lg:text-2xl">{getPageTitle()}</h1>
                        
                        {/* Header Actions */}
                        <div className="flex items-center gap-2">
                            <NotificationCenter userId={user?.id} />
                        </div>
                    </div>
                </header>
                
                {/* Page Content */}
                <main className="p-4 lg:p-8">
                    <Outlet />
                </main>
            </div>
            
            {/* Mobile Bottom Navigation */}
            <nav className="mobile-nav lg:hidden">
                <div className="flex items-center justify-around">
                    {mobileNav.map((item) => {
                        if (item.isMenu) {
                            return (
                                <Sheet key="menu" open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                                    <SheetTrigger asChild>
                                        <button className="mobile-nav-item" data-testid="mobile-nav-menu">
                                            <Menu className="w-5 h-5" />
                                            <span>Menu</span>
                                        </button>
                                    </SheetTrigger>
                                    <SheetContent side="bottom" className="h-[70vh]">
                                        <div className="pt-4">
                                            <h3 className="font-semibold text-lg mb-4">Menu</h3>
                                            <nav className="space-y-1">
                                                {navigation.map((navItem) => (
                                                    <NavItem 
                                                        key={navItem.name} 
                                                        item={navItem} 
                                                        onClick={() => setMobileMenuOpen(false)}
                                                    />
                                                ))}
                                            </nav>
                                        </div>
                                    </SheetContent>
                                </Sheet>
                            );
                        }
                        
                        const isActive = location.pathname === item.href;
                        return (
                            <NavLink
                                key={item.name}
                                to={item.href}
                                className={`mobile-nav-item ${isActive ? 'active' : ''}`}
                                data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                            >
                                <item.icon className="w-5 h-5" />
                                <span>{item.name}</span>
                            </NavLink>
                        );
                    })}
                </div>
            </nav>
        </div>
    );
};

export default Layout;
